import { createSelector } from "@reduxjs/toolkit";
import { selectSearchState } from "../../selectors/shared";
import {
  isFacetPriceRange,
  isCurrentPriceRangeSelected,
  getSelectedPriceRangeFacet
} from "../../priceRange/selectors";
export { default as getAllAppliedRefinements } from "./getAllAppliedRefinements";
export { getSort } from "../../query/selectors";

const emptyObject = {};
const emptyArray = [];

export const selectSearchFiltersDomain = state => {
  const search = selectSearchState(state);

  return {
    facets: search.facets ?? emptyArray,
    selectedFacets: search.selectedFacets ?? emptyArray,
    selectedSort: search.selectedSort ?? emptyObject,
    dismissedFloors: search.dismissedFloors ?? emptyObject
  };
};

export const selectSearchHasFacets = state =>
  selectSearchFiltersDomain(state).facets.length > 0;

export const getSelectedFacetValuesByFacet = (state, facet) => {
  const selectedFacetValues = [];
  state.search.selectedFacets
    .filter(selectedFacet => selectedFacet.facetId === facet.id)
    .forEach(selectedValue => {
      const currentFacetValueIndex = selectedFacetValues.findIndex(
        facetValue => facetValue.id === selectedValue.valueId
      );

      const currentFacetValue = facet.facetValues.find(
        facetValue => facetValue.id === selectedValue.valueId
      );
      if (currentFacetValueIndex > -1) {
        selectedFacetValues[currentFacetValueIndex] = {
          ...currentFacetValue,
          isSelected: selectedValue.isSelected
        };
      } else {
        selectedFacetValues.push({
          ...currentFacetValue,
          isSelected: selectedValue.isSelected
        });
      }
    });

  return selectedFacetValues;
};

const selectFacets = state => state.search.facets;
const selectSelectedFacets = state => state.search.selectedFacets;

export const hasSelectedFacets = createSelector([selectFacets], facets =>
  facets.some(({ hasSelectedValues }) => hasSelectedValues)
);

export const getSelectedFacets = createSelector([selectFacets], facets =>
  facets.filter(facet => facet.hasSelectedValues)
);

const getSelectedPriceRangeFacetValue = state =>
  state.search?.range?.currentprice ? getSelectedPriceRangeFacet(state) : null;

export const getSelectedFacetValues = createSelector(
  [getSelectedFacets, getSelectedPriceRangeFacetValue],
  (selectedFacets, selectedPriceRangeFacet) => {
    const selectedFacetValues = selectedFacets
      .filter(facet => !isFacetPriceRange(facet))
      .flatMap(item =>
        item.facetValues.map(facetValue => ({
          ...facetValue,
          facetId: item.id
        }))
      )
      .filter(facetValue => facetValue.isSelected === true);

    return selectedPriceRangeFacet
      ? selectedFacetValues.concat({
          ...selectedPriceRangeFacet,
          facetId: "currentprice"
        })
      : selectedFacetValues;
  }
);

export const hasFacets = state => state.search.facets.length > 0;

const filterZeroCountOrNotSelected = facetValues =>
  facetValues.filter(
    facetValue => facetValue.count > 0 || facetValue.isSelected
  );

const applySelectionsToFacetValues = (facetValues, matchingSelections) =>
  facetValues.map(facetValue => {
    const matchingSelection = matchingSelections.find(
      selection => selection.valueId === facetValue.id
    );

    return matchingSelection
      ? {
          ...facetValue,
          isSelected: matchingSelection.isSelected
        }
      : facetValue;
  });

export const getFacets = createSelector(
  [selectFacets, selectSelectedFacets, state => state],
  (facets, selectedFacets, state) => {
    if (!selectedFacets.length) {
      return facets.map(facet => ({
        ...facet,
        facetValues: filterZeroCountOrNotSelected(facet.facetValues)
      }));
    }

    return facets.map(facet => {
      const matchingSelections = selectedFacets.filter(
        selection => selection.facetId === facet.id
      );
      const selectedFacetValues = applySelectionsToFacetValues(
        facet.facetValues,
        matchingSelections
      );
      const filteredFacetValues = filterZeroCountOrNotSelected(
        selectedFacetValues
      );
      const hasSelectedValuesForFacet = isFacetPriceRange(facet)
        ? isCurrentPriceRangeSelected(state)
        : filteredFacetValues.some(facetValue => facetValue.isSelected);

      return {
        ...facet,
        facetValues: filteredFacetValues,
        hasSelectedValues: hasSelectedValuesForFacet
      };
    });
  }
);

export const getFacetSelectedValues = facet => {
  const { hasSelectedValues, facetValues } = facet;

  return hasSelectedValues
    ? facetValues
        .filter(({ isSelected }) => isSelected)
        .map(({ name }) => name)
        .join(", ")
    : "";
};

export const getFacetTotalSelected = facet => {
  const { hasSelectedValues, facetValues } = facet;

  return hasSelectedValues
    ? facetValues.filter(({ isSelected }) => isSelected).length
    : 0;
};
